import { useContext, useEffect, useState } from "react";
import { withAppHOC, withAccountHOC } from "../../hoc";
import {
  Button,
  Fieldset,
  Form,
  Label,
  MessageBox,
  MobileInput,
  PasswordInput,
  TextInput,
} from "../../components/FormElements";
import Map from "../../components/Map/Map";
import Loader from "../../components/Loader/Loader";
import {
  StyledAddressesContainer,
  StyledAddButton,
  StyledAddAddressContainer,
  StyledMapContainer,
  StyledAddressContainer,
  StyledGrid,
} from "../../components/Styled";
import API from "../../api";
import { RESPONSE_STATUSES } from "../../constants";
import AddressCard from "../../components/Cards/AddressCard";
import Modal from "../../components/Modal";
import { AppContext } from "../../context";
import { getAuthData } from "../../utils";

const Addresses = () => {
  const { userProfile } = getAuthData();
  const { fullName, mobileNumber } = userProfile;
  const [state, setState] = useState({
    isLoading: false,
    apiResponseStatus: "",
    apiResponseMessage: "",
    addresses: [],
    showModal: false,
    id: "",
    pocName: "",
    pocNumber: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
    location: "",
    stage: "",
  });

  const setStateData = (key, value) => {
    setState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const fetchAddresses = async () => {
    try {
      setStateData("isLoading", true);
      const response = await API.get("/addresses");
      const { status, message, data } = response?.data;
      if (status === RESPONSE_STATUSES.SUCCESS) {
        setStateData("addresses", data);
      } else {
        setStateData("apiResponseStatus", RESPONSE_STATUSES.FAILED);
        setStateData("apiResponseMessage", message);
      }
    } catch (error) {
      console.error(error);
      setStateData("apiResponseStatus", RESPONSE_STATUSES.FAILED);
      setStateData("apiResponseMessage", error.message);
    } finally {
      setStateData("isLoading", false);
    }
  };

  const addAddress = async (e) => {
    try {
      e?.preventDefault();
      setStateData("isLoading", true);
      const response = await API.post(`/addresses`, {
        pocName: state.pocName,
        pocNumber: state.pocNumber,
        addressLine1: state.addressLine1,
        addressLine2: state.addressLine2,
        city: state.city,
        state: state.state,
        country: state.country,
        pincode: state.pincode,
        location:
          typeof state.location !== "string"
            ? JSON.stringify(state.location)
            : state.location,
      });
      const { status, message, data } = response?.data;
      if (status === RESPONSE_STATUSES.SUCCESS) {
        setStateData("addresses", data);
        setTimeout(() => {
          setStateData("showModal", false);
          setStateData("apiResponseStatus", "");
          setStateData("apiResponseMessage", "");
        }, 3000);
      }
      setStateData("apiResponseStatus", status);
      setStateData("apiResponseMessage", message);
    } catch (error) {
      console.error(error);
      setStateData("apiResponseStatus", RESPONSE_STATUSES.FAILED);
      setStateData("apiResponseMessage", error.message);
    } finally {
      setStateData("isLoading", false);
    }
  };

  const editAddress = (id) => {
    const addressDetails = state.addresses.find((item) => item.id === id);
    if (addressDetails !== -1) {
      setState((prevState) => ({
        ...prevState,
        id: addressDetails?.id,
        pocName: addressDetails?.pocName,
        pocNumber: addressDetails?.pocNumber,
        addressLine1: addressDetails?.addressLine1,
        addressLine2: addressDetails?.addressLine2,
        city: addressDetails?.city,
        state: addressDetails?.state,
        country: addressDetails?.country,
        pincode: addressDetails?.pincode,
        location:
          addressDetails?.location &&
          typeof addressDetails?.location === "string"
            ? JSON.parse(addressDetails?.location)
            : addressDetails?.location,
        showModal: true,
        stage: 1,
      }));
    } else {
      alert("Address not found, please reload");
    }
  };

  const updateAddress = async (e) => {
    try {
      e?.preventDefault();
      setStateData("isLoading", true);
      const response = await API.put(`/addresses/${state.id}`, {
        pocName: state.pocName,
        pocNumber: state.pocNumber,
        addressLine1: state.addressLine1,
        addressLine2: state.addressLine2,
        city: state.city,
        state: state.state,
        country: state.country,
        pincode: state.pincode,
        location:
          typeof state.location !== "string"
            ? JSON.stringify(state.location)
            : state.location,
      });
      const { status, message, data } = response?.data;
      if (status === RESPONSE_STATUSES.SUCCESS) {
        setStateData("addresses", data);
        setTimeout(() => {
          setStateData("showModal", false);
          setStateData("apiResponseStatus", "");
          setStateData("apiResponseMessage", "");
        }, 3000);
      }
      setStateData("apiResponseStatus", status);
      setStateData("apiResponseMessage", message);
    } catch (error) {
      console.error(error);
      setStateData("apiResponseStatus", RESPONSE_STATUSES.FAILED);
      setStateData("apiResponseMessage", error.message);
    } finally {
      setStateData("isLoading", false);
    }
  };

  const deleteAddress = async (id) => {
    try {
      setStateData("isLoading", true);
      const response = await API.delete(`/addresses/${id}`);
      const { status, message, data } = response?.data;
      if (status === RESPONSE_STATUSES.SUCCESS) {
        setStateData("addresses", data);
      } else {
        setStateData("apiResponseStatus", RESPONSE_STATUSES.FAILED);
        setStateData("apiResponseMessage", message);
      }
    } catch (error) {
      console.error(error);
      setStateData("apiResponseStatus", RESPONSE_STATUSES.FAILED);
      setStateData("apiResponseMessage", error.message);
    } finally {
      setStateData("isLoading", false);
    }
  };

  useEffect(() => {
    fetchAddresses();
  }, []);

  return (
    <>
      <Loader isLoading={state.isLoading} />
      <StyledAddressesContainer>
        <StyledAddButton
          onClick={() =>
            setState((prevState) => ({
              ...prevState,
              id: "",
              pocName: fullName,
              pocNumber: mobileNumber,
              addressLine1: "",
              addressLine2: "",
              city: "",
              state: "",
              country: "",
              pincode: "",
              location: "",
              stage: 1,
              showModal: true,
            }))
          }
        >
          + Add Address
        </StyledAddButton>
        {state.addresses.map((item, index) => {
          return (
            <AddressCard
              {...item}
              key={index}
              onClickDelete={deleteAddress}
              onClickEdit={editAddress}
            />
          );
        })}
      </StyledAddressesContainer>
      <Modal
        modalStatus={state.showModal}
        setModalStatus={() => {
          setState((prevState) => ({
            ...prevState,
            showModal: false,
            id: "",
            pocName: "",
            pocNumber: "",
            addressLine1: "",
            addressLine2: "",
            city: "",
            state: "",
            country: "",
            pincode: "",
            location: "",
            stage: "",
          }));
        }}
        hideCloseButton
      >
        <StyledAddAddressContainer>
          <h1>{state?.id ? "Edit Address" : "Add Address"}</h1>
          {state.stage === 1 && (
            <>
              <StyledMapContainer>
                <Map
                  location={state?.location}
                  setState={(data) =>
                    setState((prevState) => ({ ...prevState, ...data }))
                  }
                />
              </StyledMapContainer>
              <Button
                onClick={() =>
                  setState((prevState) => ({ ...prevState, stage: 2 }))
                }
              >
                Continue
              </Button>
            </>
          )}
          {state.stage === 2 && (
            <StyledAddressContainer>
              <Form
                action="#"
                method="POST"
                onSubmit={state.id ? updateAddress : addAddress}
              >
                <StyledGrid>
                  <Fieldset>
                    <Label required>Full Name</Label>
                    <TextInput
                      value={state.pocName}
                      onChange={(value) => setStateData("pocName", value)}
                      placeholder="Full Name"
                      disabled={state.isLoading}
                    />
                  </Fieldset>
                  <Fieldset>
                    <Label required>Mobile Number</Label>
                    <MobileInput
                      value={state.pocNumber}
                      onChange={(value) => setStateData("pocNumber", value)}
                      placeholder="Mobile Number"
                      disabled={state.isLoading}
                    />
                  </Fieldset>
                </StyledGrid>
                <Fieldset>
                  <Label required>Address Line 1</Label>
                  <TextInput
                    value={state.addressLine1}
                    onChange={(value) => setStateData("addressLine1", value)}
                    placeholder="Address Line 1"
                    disabled={state.isLoading}
                  />
                </Fieldset>
                <Fieldset>
                  <Label>Address Line 2</Label>
                  <TextInput
                    value={state.addressLine2}
                    onChange={(value) => setStateData("addressLine2", value)}
                    placeholder="Address Line 2"
                    disabled={state.isLoading}
                  />
                </Fieldset>
                <StyledGrid>
                  <Fieldset>
                    <Label required>City</Label>
                    <TextInput
                      value={state.city}
                      onChange={(value) => setStateData("city", value)}
                      placeholder="City"
                      disabled={state.isLoading}
                    />
                  </Fieldset>
                  <Fieldset>
                    <Label required>State</Label>
                    <TextInput
                      value={state.state}
                      onChange={(value) => setStateData("state", value)}
                      placeholder="State"
                      disabled={state.isLoading}
                    />
                  </Fieldset>
                </StyledGrid>
                <StyledGrid>
                  <Fieldset>
                    <Label required>Country</Label>
                    <TextInput
                      value={state.country}
                      onChange={(value) => setStateData("country", value)}
                      placeholder="Country"
                      disabled={state.isLoading}
                    />
                  </Fieldset>
                  <Fieldset>
                    <Label required>Pincode</Label>
                    <TextInput
                      value={state.pincode}
                      onChange={(value) => setStateData("pincode", value)}
                      placeholder="Pincode"
                      disabled={state.isLoading}
                    />
                  </Fieldset>
                </StyledGrid>
                {state.apiResponseStatus && state.apiResponseMessage && (
                  <Fieldset>
                    <MessageBox
                      status={state.apiResponseStatus}
                      message={state.apiResponseMessage}
                    />
                  </Fieldset>
                )}
                <Fieldset>
                  <Button disabled={state.isLoading}>Save</Button>
                </Fieldset>
              </Form>
            </StyledAddressContainer>
          )}
        </StyledAddAddressContainer>
      </Modal>
    </>
  );
};

export default withAppHOC(withAccountHOC(Addresses, { title: "My Addresses" }));
